import { useToast } from '@components/index'
import { Table } from '@components/table'
import { PageRead } from '@components/table/types'
import { usePageReadPayload } from '@components/table/usePageReadPayload'
import {
  useBlockPartnerReferrals,
  useDemotePartner,
  usePartners
} from '@services/referral'
import type { UseQueryResult } from '@tanstack/react-query'
import {
  Ban,
  CircleCheck,
  MoreHorizontal,
  Pencil,
  UserMinus,
  UserRound
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

import { EditPartnerModal } from './EditPartnerModal'

interface Partner {
  id: string
  email: string
  first_name?: string
  last_name?: string
  created_at: string
  total_referrals: number
  active_referrals: number
  link_referrals: number
  instagram_referrals: number
  referrals_with_active_applications: number
}

function CountPill({ value, tone }: { value: number; tone: string }) {
  return (
    <span
      className={cn(
        'inline-flex min-w-[2rem] items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        value > 0 ? tone : 'bg-ink/[0.07] text-ink/50'
      )}
    >
      {value ?? 0}
    </span>
  )
}

export function PartnersTable() {
  const { t } = useTranslation('admin')
  const toast = useToast()
  const [searchParams] = useSearchParams()
  const { size } = usePageReadPayload(searchParams)
  const offset = Number(searchParams.get('offset') || 0)
  const search = searchParams.get('search') || undefined

  const [editPartnerId, setEditPartnerId] = useState<string | null>(null)

  const query = usePartners({ search, limit: size, offset })
  const blockMutation = useBlockPartnerReferrals()
  const demoteMutation = useDemotePartner()

  const fullName = (p: Partner) =>
    `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim() || p.email

  const setBlocked = (partner: Partner, block: boolean) => {
    blockMutation.mutate({
      id: partner.id,
      body: { block },
      params: {
        onSuccess: () =>
          toast.success({
            message: block
              ? t('referrals.partners.blockSuccess')
              : t('referrals.partners.unblockSuccess')
          }),
        onError: () =>
          toast.error({
            message: block
              ? t('referrals.partners.blockError')
              : t('referrals.partners.unblockError')
          })
      }
    })
  }

  const handleBlock = (partner: Partner) => {
    if (
      window.confirm(
        t('referrals.partners.blockDescription', { name: fullName(partner) })
      )
    ) {
      setBlocked(partner, true)
    }
  }

  const handleDemote = (partner: Partner) => {
    if (
      window.confirm(
        t('referrals.partners.demoteDescription', { name: fullName(partner) })
      )
    ) {
      demoteMutation.mutate({
        id: partner.id,
        params: {
          onSuccess: () =>
            toast.success({ message: t('referrals.partners.demoteSuccess') }),
          onError: () =>
            toast.error({ message: t('referrals.partners.demoteError') })
        }
      })
    }
  }

  const columns = [
    {
      title: t('referrals.partners.partner'),
      key: 'partner',
      render: (_: unknown, partner: Partner) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <UserRound className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <div className="truncate font-medium text-ink">
              {fullName(partner)}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {partner.email}
            </div>
          </div>
        </div>
      )
    },
    {
      title: t('referrals.partners.linkReferrals'),
      dataIndex: 'link_referrals',
      align: 'center' as const,
      render: (count: number) => (
        <CountPill value={count} tone="bg-info-soft text-info" />
      )
    },
    {
      title: t('referrals.partners.instagramReferrals'),
      dataIndex: 'instagram_referrals',
      align: 'center' as const,
      render: (count: number) => (
        <CountPill value={count} tone="bg-purple-soft text-purple" />
      )
    },
    {
      title: t('referrals.partners.referralsWithActiveApplications'),
      dataIndex: 'referrals_with_active_applications',
      align: 'center' as const,
      render: (count: number) => (
        <CountPill value={count} tone="bg-success-soft text-success" />
      )
    },
    {
      title: t('referrals.partners.joinedDate'),
      dataIndex: 'created_at',
      render: (date: string) => (
        <span className="text-sm text-muted-foreground">
          {date ? new Date(date).toLocaleDateString('sk-SK') : '—'}
        </span>
      )
    },
    {
      title: t('common.actions'),
      key: 'actions',
      align: 'center' as const,
      width: 80,
      render: (_: unknown, partner: Partner) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink/60 transition-colors hover:bg-ink/[0.06] hover:text-ink"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditPartnerId(partner.id)}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('referrals.partners.updateReward')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBlock(partner)}>
                <Ban className="mr-2 h-4 w-4" />
                {t('referrals.partners.blockReferrals')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setBlocked(partner, false)}>
                <CircleCheck className="mr-2 h-4 w-4" />
                {t('referrals.partners.unblockReferrals')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDemote(partner)}
                className="text-destructive focus:text-destructive"
              >
                <UserMinus className="mr-2 h-4 w-4" />
                {t('referrals.partners.demotePartner')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }
  ]

  return (
    <>
      <Table<PageRead<Partner>>
        query={query as unknown as UseQueryResult<PageRead<Partner>, Error>}
        columns={columns}
        id="partners"
        hasClick={false}
        hasSearch={false}
      />

      <EditPartnerModal
        open={!!editPartnerId}
        onClose={() => setEditPartnerId(null)}
        partnerId={editPartnerId ?? ''}
      />
    </>
  )
}
